import React, { createContext, useState, useContext, ReactNode } from 'react';
import { PortfolioData } from '../services/apiService';
import { auth } from '../services/firebase';
import { signOut } from 'firebase/auth';

interface PortfolioContextType {
  portfolioData: PortfolioData | null;
  setPortfolioData: (data: PortfolioData | null) => void;
  lastSyncedAccountId: string | null;
  setLastSyncedAccountId: (id: string | null) => void;
  userName: string;
  setUserName: (name: string) => void;
  isSynced: boolean;
  logout: () => void;
  clearSessionCache: () => void;
}

const PortfolioContext = createContext<PortfolioContextType | undefined>(undefined);

export const PortfolioProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [portfolioData, setPortfolioData] = useState<PortfolioData | null>(() => {
    const saved = localStorage.getItem('portfolio_data');
    return saved ? JSON.parse(saved) : null;
  });
  
  const [lastSyncedAccountId, setLastSyncedAccountIdState] = useState<string | null>(() => {
    const saved = localStorage.getItem('lastSyncedAccountId');
    if (!saved || saved === 'null' || saved === 'undefined') return null;
    return saved;
  });

  const [userName, setUserName] = useState<string>(() => localStorage.getItem('trader_name') || '');

  const handleSetPortfolioData = (data: PortfolioData | null) => {
    setPortfolioData(data);
    if (data) {
      localStorage.setItem('portfolio_data', JSON.stringify(data));
    } else {
      localStorage.removeItem('portfolio_data');
    }
  };

  const handleSetLastSyncedAccountId = (id: string | null) => {
    setLastSyncedAccountIdState(id);
    if (id !== null) {
      localStorage.setItem('lastSyncedAccountId', id);
    } else {
      localStorage.removeItem('lastSyncedAccountId');
    }
  };

  const handleSetUserName = (name: string) => {
    setUserName(name);
    localStorage.setItem('trader_name', name);
  };

  const clearSessionCache = () => {
    setPortfolioData(null);
    setLastSyncedAccountIdState(null);
    setUserName('');

    // Clear all user-specific localStorage items
    const userKeys = [
      'portfolio_data',
      'lastSyncedAccountId',
      'trader_name',
      'user_form_data',
      'app_step',
      'app_substep',
      'auth_token',
      'selectedAccountId',
      'trader_avatar_url',
      'activity_logs',
      'reviewed_trades',
      'subscription_data',
      'has_cex_accounts',
      'pricing_popup_shown'
    ];
    userKeys.forEach(key => localStorage.removeItem(key));
  };

  const logout = () => {
    clearSessionCache();

    // Sign out of Firebase Auth to ensure a clean session
    signOut(auth).catch(err => console.error('Firebase signOut error:', err));

    window.location.href = '/login';
  };

  return (
    <PortfolioContext.Provider 
      value={{ 
        portfolioData, 
        setPortfolioData: handleSetPortfolioData,
        lastSyncedAccountId,
        setLastSyncedAccountId: handleSetLastSyncedAccountId,
        userName,
        setUserName: handleSetUserName,
        isSynced: portfolioData !== null,
        logout,
        clearSessionCache
      }}
    >
      {children}
    </PortfolioContext.Provider>
  );
};

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (context === undefined) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
};
